import { fetch_request } from '../../../service/request';
import {
  applyMessengerV2Snapshot,
  normalizeMessengerV2SuiteId,
  resolveMessengerV2Manager,
} from './messengerV2MlsGateway.js';

const DEFAULT_MLS_CIPHERSUITE = 0x004d;
const CONVERSATION_PAGE_SIZE = 100;
const MAX_CONVERSATION_PAGES = 50;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const booleanWithDefault = (value, fallback = false) =>
  value === undefined || value === null ? fallback : Boolean(value);

const createHttpError = (response, fallbackMessage = null) => {
  const error = new Error(fallbackMessage || `HTTP ${response.status}`);
  error.status = response.status;
  error.response = response;
  return error;
};

const toServerSeqOrNull = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

const mapContact = (item = {}) => ({
  id: toStringOrNull(item.connected_user_id ?? item.id ?? null),
  slug: toStringOrNull(item.connected_user_slug ?? item.slug ?? null),
  name: toStringOrNull(item.user_name ?? item.name ?? null) || 'Unknown user',
  relationKey: toStringOrNull(item.reciprocal_relation_key ?? item.relationKey ?? null),
  relationName: toStringOrNull(item.reciprocal_relation_name ?? item.relationName ?? null),
  avatar: toStringOrNull(
    item.avatar ??
      item.avatar_url ??
      item.connected_user_avatar ??
      item.connected_user_avatar_url ??
      null,
  ),
  avatar_url: toStringOrNull(
    item.avatar_url ??
      item.avatar ??
      item.connected_user_avatar_url ??
      item.connected_user_avatar ??
      null,
  ),
});

const normalizeGroup = (group = {}) => ({
  ...group,
  id: toStringOrNull(group.id ?? group.group_id ?? group.groupId ?? null),
  name:
    toStringOrNull(group.name ?? group.title ?? null) ||
    toStringOrNull(group.uuid ?? null) ||
    'Group',
  description: toStringOrNull(group.description ?? null),
  visibility: toStringOrNull(group.visibility ?? null),
  status: toStringOrNull(group.status ?? null),
  membersCount:
    Number(group.group_members_count ?? group.groupMembersCount ?? group.members_count ?? 0) || 0,
  requirePostApproval: Boolean(group.require_post_approval ?? group.requirePostApproval ?? false),
  require_post_approval: Boolean(group.require_post_approval ?? group.requirePostApproval ?? false),
  memberCanEditGroupSettings: booleanWithDefault(
    group.member_can_edit_group_settings ?? group.memberCanEditGroupSettings,
    true,
  ),
  member_can_edit_group_settings: booleanWithDefault(
    group.member_can_edit_group_settings ?? group.memberCanEditGroupSettings,
    true,
  ),
  memberCanAddMembers: booleanWithDefault(
    group.member_can_add_members ?? group.memberCanAddMembers,
    true,
  ),
  member_can_add_members: booleanWithDefault(
    group.member_can_add_members ?? group.memberCanAddMembers,
    true,
  ),
  memberCanInviteViaLink: booleanWithDefault(
    group.member_can_invite_via_link ?? group.memberCanInviteViaLink,
    true,
  ),
  member_can_invite_via_link: booleanWithDefault(
    group.member_can_invite_via_link ?? group.memberCanInviteViaLink,
    true,
  ),
  requireMemberApproval: booleanWithDefault(
    group.require_member_approval ?? group.requireMemberApproval,
    true,
  ),
  require_member_approval: booleanWithDefault(
    group.require_member_approval ?? group.requireMemberApproval,
    true,
  ),
  ownerId: toStringOrNull(group.owner_id ?? group.ownerId ?? group.owner?.id ?? null),
  conversationId: toStringOrNull(group.conversation_id ?? group.conversationId ?? null),
  conversation_id: toStringOrNull(group.conversation_id ?? group.conversationId ?? null),
  avatar_url: toStringOrNull(group.avatar_url ?? group.avatarUrl ?? group.avatar ?? null),
  avatar: toStringOrNull(group.avatar ?? group.avatar_url ?? group.avatarUrl ?? null),
  updatedAt: toStringOrNull(group.updated_at ?? group.updatedAt ?? null),
  createdAt: toStringOrNull(group.created_at ?? group.createdAt ?? null),
});

const normalizeGroupCategory = (category = {}) => ({
  ...category,
  id: toStringOrNull(category.id ?? category.group_category_id ?? category.groupCategoryId ?? null),
  name: toStringOrNull(category.name ?? category.title ?? null) || 'General',
});

const normalizePresence = (presence = null) => {
  if (!presence || typeof presence !== 'object') return null;
  return {
    state: toStringOrNull(presence.state ?? null) || 'unknown',
    since: toStringOrNull(presence.since ?? null),
    lastSeenAt: toStringOrNull(presence.last_seen_at ?? presence.lastSeenAt ?? null),
    onlineCount:
      typeof presence.online_count === 'number'
        ? presence.online_count
        : typeof presence.onlineCount === 'number'
          ? presence.onlineCount
          : null,
    totalCount:
      typeof presence.total_count === 'number'
        ? presence.total_count
        : typeof presence.totalCount === 'number'
          ? presence.totalCount
          : null,
  };
};

const normalizeConversation = (conversation = {}) => ({
  ...conversation,
  id: toStringOrNull(conversation.id ?? null),
  title: toStringOrNull(conversation.title ?? conversation.name ?? null) || 'Unknown conversation',
  subtitle: toStringOrNull(conversation.subtitle ?? null),
  last_message_preview: toStringOrNull(conversation.last_message_preview ?? null),
  last_server_msg_id: toStringOrNull(conversation.last_server_msg_id ?? null),
  last_server_seq: toServerSeqOrNull(conversation.last_server_seq ?? null),
  last_message_at: toStringOrNull(conversation.last_message_at ?? null),
  updated_at: toStringOrNull(conversation.updated_at ?? null),
  avatar_url: toStringOrNull(
    conversation.avatar_url ??
      conversation.avatarUrl ??
      conversation.avatar ??
      conversation.group?.avatar_url ??
      conversation.group?.avatarUrl ??
      conversation.group?.avatar ??
      null,
  ),
  unread_count: Number(conversation.unread_count ?? 0) || 0,
  typing: conversation.typing === true,
  typingActivity:
    conversation.typingActivity ??
    conversation.typing_activity ??
    (conversation.typing ? 'typing' : null),
  blocked_by_me: Boolean(conversation.blocked_by_me ?? conversation.blockedByMe ?? false),
  blockedByMe: Boolean(conversation.blockedByMe ?? conversation.blocked_by_me ?? false),
  blocked_me: Boolean(conversation.blocked_me ?? conversation.blockedMe ?? false),
  blockedMe: Boolean(conversation.blockedMe ?? conversation.blocked_me ?? false),
  presence: normalizePresence(conversation.presence),
  participants: Array.isArray(conversation.participants)
    ? conversation.participants.map((participant) => ({
        ...participant,
        id: toStringOrNull(participant?.id ?? participant?.user_id ?? null),
        last_delivered_at: toStringOrNull(
          participant?.last_delivered_at ?? participant?.lastDeliveredAt ?? null,
        ),
        last_read_at: toStringOrNull(participant?.last_read_at ?? participant?.lastReadAt ?? null),
        last_delivered_message_id: toStringOrNull(
          participant?.last_delivered_message_id ??
            participant?.lastDeliveredMessageId ??
            participant?.last_delivered_server_msg_id ??
            participant?.lastDeliveredServerMsgId ??
            null,
        ),
        last_read_message_id: toStringOrNull(
          participant?.last_read_message_id ??
            participant?.lastReadMessageId ??
            participant?.last_read_server_msg_id ??
            participant?.lastReadServerMsgId ??
            null,
        ),
        last_delivered_server_msg_id: toStringOrNull(
          participant?.last_delivered_server_msg_id ??
            participant?.lastDeliveredServerMsgId ??
            null,
        ),
        last_decrypted_server_msg_id: toStringOrNull(
          participant?.last_decrypted_server_msg_id ??
            participant?.lastDecryptedServerMsgId ??
            null,
        ),
        last_read_server_msg_id: toStringOrNull(
          participant?.last_read_server_msg_id ?? participant?.lastReadServerMsgId ?? null,
        ),
        last_delivered_server_seq: toServerSeqOrNull(
          participant?.last_delivered_server_seq ?? participant?.lastDeliveredServerSeq ?? null,
        ),
        last_decrypted_server_seq: toServerSeqOrNull(
          participant?.last_decrypted_server_seq ?? participant?.lastDecryptedServerSeq ?? null,
        ),
        last_read_server_seq: toServerSeqOrNull(
          participant?.last_read_server_seq ?? participant?.lastReadServerSeq ?? null,
        ),
        avatar_url: toStringOrNull(participant?.avatar_url ?? participant?.avatarUrl ?? null),
        group_member_id: toStringOrNull(
          participant?.group_member_id ?? participant?.groupMemberId ?? null,
        ),
        groupMemberId: toStringOrNull(
          participant?.groupMemberId ?? participant?.group_member_id ?? null,
        ),
        group_role: toStringOrNull(participant?.group_role ?? participant?.groupRole ?? null),
        groupRole: toStringOrNull(participant?.groupRole ?? participant?.group_role ?? null),
        group_status: toStringOrNull(participant?.group_status ?? participant?.groupStatus ?? null),
        groupStatus: toStringOrNull(participant?.groupStatus ?? participant?.group_status ?? null),
        blocked_by_me: Boolean(participant?.blocked_by_me ?? participant?.blockedByMe ?? false),
        blockedByMe: Boolean(participant?.blockedByMe ?? participant?.blocked_by_me ?? false),
        blocked_me: Boolean(participant?.blocked_me ?? participant?.blockedMe ?? false),
        blockedMe: Boolean(participant?.blockedMe ?? participant?.blocked_me ?? false),
      }))
    : [],
  pending_group_members: Array.isArray(
    conversation.pending_group_members ?? conversation.pendingGroupMembers,
  )
    ? (conversation.pending_group_members ?? conversation.pendingGroupMembers).map((member) => ({
        ...member,
        id: toStringOrNull(member?.id ?? member?.user_id ?? member?.userId ?? null),
        group_member_id: toStringOrNull(member?.group_member_id ?? member?.groupMemberId ?? null),
        groupMemberId: toStringOrNull(member?.groupMemberId ?? member?.group_member_id ?? null),
        group_role: toStringOrNull(member?.group_role ?? member?.groupRole ?? null),
        groupRole: toStringOrNull(member?.groupRole ?? member?.group_role ?? null),
        group_status: toStringOrNull(member?.group_status ?? member?.groupStatus ?? null),
        groupStatus: toStringOrNull(member?.groupStatus ?? member?.group_status ?? null),
        avatar_url: toStringOrNull(member?.avatar_url ?? member?.avatarUrl ?? null),
        invited_at: toStringOrNull(member?.invited_at ?? member?.invitedAt ?? null),
      }))
    : [],
});

export const normalizeMessengerV2Conversation = normalizeConversation;

const isSupportedMessengerV2Conversation = (conversation = null) =>
  toStringOrNull(conversation?.kind ?? conversation?.meta?.kind ?? null) !== 'room' &&
  toStringOrNull(conversation?.e2ee_protocol ?? conversation?.meta?.e2ee_protocol ?? null) ===
    'mls';

const sortConversations = (items = []) =>
  [...items].sort((left, right) => {
    const leftTime = new Date(left?.last_message_at ?? left?.updated_at ?? 0).getTime();
    const rightTime = new Date(right?.last_message_at ?? right?.updated_at ?? 0).getTime();
    return rightTime - leftTime;
  });

const patchConversationWithMlsMeta = (
  conversation,
  snapshot,
  fallbackSuite = DEFAULT_MLS_CIPHERSUITE,
) => {
  if (!conversation || typeof conversation !== 'object') {
    return conversation;
  }

  const groupId = toStringOrNull(snapshot?.group_id ?? snapshot?.groupId ?? null);
  if (!groupId) {
    return conversation;
  }

  const ciphersuite = normalizeMessengerV2SuiteId(
    snapshot?.ciphersuite ??
      conversation?.mls_ciphersuite ??
      conversation?.meta?.mls_ciphersuite ??
      fallbackSuite,
  );

  return {
    ...conversation,
    meta: {
      ...(conversation.meta || {}),
      mls_group_id: groupId,
      e2ee_protocol: 'mls',
      e2ee_enabled: true,
      mls_ciphersuite: ciphersuite,
      ciphersuite,
      mls_want_pq: true,
    },
    mls_group_id: groupId,
    e2ee_protocol: 'mls',
    e2ee_enabled: true,
    mls_ciphersuite: ciphersuite,
    ciphersuite,
    mls_want_pq: true,
  };
};

export const loadMessengerV2Conversations = async () => {
  const conversationsById = new Map();

  for (let page = 1; page <= MAX_CONVERSATION_PAGES; page += 1) {
    const pageQuery = page === 1 ? '' : `&page=${page}`;
    const response = await fetch_request(
      `/api/conversations?per_page=${CONVERSATION_PAGE_SIZE}${pageQuery}`,
      { cache: 'no-store' },
    );
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const payload = await response.json();
    const rawItems = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.conversations)
        ? payload.conversations
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
    const knownCount = conversationsById.size;
    rawItems.map(normalizeConversation).forEach((conversation) => {
      if (conversation.id) conversationsById.set(conversation.id, conversation);
    });
    if (rawItems.length < CONVERSATION_PAGE_SIZE || conversationsById.size === knownCount) break;
  }

  return sortConversations(
    Array.from(conversationsById.values()).filter((item) =>
      isSupportedMessengerV2Conversation(item),
    ),
  );
};

export const loadMessengerV2Contacts = async ({ currentUserSlug } = {}) => {
  const normalizedSlug = toStringOrNull(currentUserSlug);
  if (!normalizedSlug) {
    return [];
  }

  const response = await fetch_request(`/api/user_connections?slug=${normalizedSlug}`, {
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const data = await response.json();
  const items = Array.isArray(data?.user_connections) ? data.user_connections : [];
  return items.map(mapContact).filter((item) => item.id);
};

export const loadMessengerV2Group = async ({ groupId } = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required');
  }

  const response = await fetch_request(`/api/groups/${encodeURIComponent(normalizedGroupId)}`, {
    cache: 'no-store',
    timeoutMs: 8000,
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = await response.json();
  return normalizeGroup(payload?.group || payload);
};

export const loadMessengerV2GroupAvailableUsers = async ({
  groupId,
  search = '',
  limit = 50,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required');
  }

  const params = new URLSearchParams();
  const normalizedSearch = toStringOrNull(search);
  if (normalizedSearch) params.set('search', normalizedSearch);
  if (limit) params.set('limit', String(limit));
  const queryString = params.toString();
  const response = await fetch_request(
    `/api/groups/${encodeURIComponent(normalizedGroupId)}/group_members/available_users${queryString ? `?${queryString}` : ''}`,
    {
      cache: 'no-store',
      timeoutMs: 8000,
    },
  );
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = await response.json();
  const users = Array.isArray(payload?.users)
    ? payload.users
    : Array.isArray(payload)
      ? payload
      : [];
  return users
    .map((user) => {
      const id = toStringOrNull(user?.id ?? user?.user_id ?? user?.userId ?? null);
      if (!id) return null;
      const avatar = toStringOrNull(
        user?.avatar_url ?? user?.avatar ?? user?.display_picture ?? user?.displayPicture ?? null,
      );
      return {
        ...user,
        id,
        user_id: id,
        userId: id,
        name:
          toStringOrNull(user?.name ?? user?.full_name ?? user?.fullName ?? null) ||
          toStringOrNull(user?.slug ?? null) ||
          'Member',
        avatar,
        avatar_url: avatar,
      };
    })
    .filter(Boolean);
};

export const loadMessengerV2GroupCategories = async () => {
  const response = await fetch_request('/api/group_categories', {
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = await response.json();
  const rawItems = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.group_categories)
      ? payload.group_categories
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

  return rawItems.map(normalizeGroupCategory).filter((item) => item.id);
};

export const createMessengerV2Group = async ({
  name,
  description = '',
  selectedContactIds = [],
  avatarFile = null,
} = {}) => {
  const normalizedName = toStringOrNull(name)?.trim();
  if (!normalizedName) {
    throw new Error('name is required');
  }

  let body;
  let headers;
  const conversationPayload = {
    title: normalizedName,
    description: toStringOrNull(description)?.trim() || '',
    participant_ids: Array.from(
      new Set((selectedContactIds || []).map(toStringOrNull).filter(Boolean)),
    ),
  };
  if (avatarFile) {
    body = new FormData();
    body.append('conversation[title]', conversationPayload.title);
    body.append('conversation[description]', conversationPayload.description);
    conversationPayload.participant_ids.forEach((participantId) => {
      body.append('conversation[participant_ids][]', participantId);
    });
    body.append('conversation[avatar]', avatarFile);
  } else {
    headers = { 'Content-Type': 'application/json' };
    body = JSON.stringify({ conversation: conversationPayload });
  }

  const response = await fetch_request('/api/conversations/group', {
    method: 'POST',
    ...(headers ? { headers } : {}),
    body,
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = await response.json();
  return {
    ...payload,
    conversation: normalizeConversation(payload?.conversation || payload),
  };
};

export const addMessengerV2ConversationParticipants = async ({
  conversationId,
  userIds = [],
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }
  const participantIds = Array.from(new Set(userIds.map(toStringOrNull).filter(Boolean)));
  const response = await fetch_request(
    `/api/conversations/${encodeURIComponent(normalizedConversationId)}/participants`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ participant_ids: participantIds }),
    },
  );
  if (!response.ok) {
    throw createHttpError(response);
  }

  const payload = await response.json();
  return {
    ...payload,
    conversation: normalizeConversation(payload?.conversation || payload),
  };
};

export const updateMessengerV2GroupAvatar = async ({
  groupId,
  avatarFile = null,
  removeAvatar = false,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required');
  }

  const body = new FormData();
  if (avatarFile) {
    body.append('group[avatar]', avatarFile, avatarFile.name || 'group-avatar.jpg');
  }
  if (removeAvatar) {
    body.append('group[remove_avatar]', 'true');
  }

  const response = await fetch_request(`/api/groups/${encodeURIComponent(normalizedGroupId)}`, {
    method: 'PATCH',
    body,
  });
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const payload = await response.clone().json();
      const payloadErrors = Array.isArray(payload?.errors)
        ? payload.errors.filter(Boolean).join(', ')
        : null;
      message = payload?.error || payloadErrors || payload?.message || message;
    } catch {}
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  const payload = await response.json();
  return normalizeGroup(payload?.group || payload);
};

export const updateMessengerV2Group = async ({
  groupId,
  name,
  description,
  requirePostApproval,
  memberCanEditGroupSettings,
  memberCanAddMembers,
  memberCanInviteViaLink,
  requireMemberApproval,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required');
  }

  const group = {};
  const normalizedName = toStringOrNull(name);
  if (normalizedName) group.name = normalizedName;
  if (description !== undefined) group.description = toStringOrNull(description)?.trim() || '';
  if (requirePostApproval !== undefined) group.require_post_approval = Boolean(requirePostApproval);
  if (memberCanEditGroupSettings !== undefined) {
    group.member_can_edit_group_settings = Boolean(memberCanEditGroupSettings);
  }
  if (memberCanAddMembers !== undefined) {
    group.member_can_add_members = Boolean(memberCanAddMembers);
  }
  if (memberCanInviteViaLink !== undefined) {
    group.member_can_invite_via_link = Boolean(memberCanInviteViaLink);
  }
  if (requireMemberApproval !== undefined) {
    group.require_member_approval = Boolean(requireMemberApproval);
  }

  const response = await fetch_request(`/api/groups/${encodeURIComponent(normalizedGroupId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ group }),
  });
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const payload = await response.clone().json();
      const payloadErrors = Array.isArray(payload?.errors)
        ? payload.errors.filter(Boolean).join(', ')
        : null;
      message = payload?.error || payloadErrors || payload?.message || message;
    } catch {}
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  const payload = await response.json();
  return normalizeGroup(payload?.group || payload);
};

export const inviteMessengerV2GroupMember = async ({
  groupId,
  userId,
  role = 'member',
  status = 'pending',
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  const normalizedUserId = toStringOrNull(userId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required');
  }
  if (!normalizedUserId) {
    throw new Error('userId is required');
  }

  const response = await fetch_request(
    `/api/groups/${encodeURIComponent(normalizedGroupId)}/group_members`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        group_member: {
          user_id: normalizedUserId,
          role,
          status,
        },
      }),
    },
  );
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = await response.json();
  return payload;
};

export const inviteMessengerV2GroupMembers = async ({
  groupId,
  userIds = [],
  role = 'member',
  status = 'pending',
} = {}) => {
  const uniqueUserIds = Array.from(new Set(userIds.map(toStringOrNull).filter(Boolean)));
  const invited = [];
  for (const userId of uniqueUserIds) {
    invited.push(await inviteMessengerV2GroupMember({ groupId, userId, role, status }));
  }
  return invited;
};

export const loadMessengerV2GroupInviteLink = async ({ groupId, conversationId } = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedGroupId && !normalizedConversationId) {
    throw new Error('groupId or conversationId is required');
  }

  const response = await fetch_request(
    normalizedGroupId
      ? `/api/groups/${encodeURIComponent(normalizedGroupId)}/invite_link`
      : `/api/conversations/${encodeURIComponent(normalizedConversationId)}/group_invite_link`,
    {
      cache: 'no-store',
      timeoutMs: 8000,
    },
  );
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = await response.json();
  return payload?.group_invite_link || payload;
};

const parseMessengerV2JsonResponse = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const buildMessengerV2RequestError = (response, payload = null) => {
  const message =
    toStringOrNull(payload?.error ?? payload?.message ?? payload?.errors?.[0] ?? null) ||
    `HTTP ${response.status}`;
  const error = new Error(message);
  error.status = response.status;
  error.code = toStringOrNull(payload?.code ?? payload?.error_code ?? payload?.errorCode ?? null);
  error.payload = payload;
  error.groupInviteLink = payload?.group_invite_link || payload?.groupInviteLink || null;
  return error;
};

export const loadMessengerV2GroupInvitePreview = async ({ token } = {}) => {
  const normalizedToken = toStringOrNull(token);
  if (!normalizedToken) {
    throw new Error('token is required');
  }

  const response = await fetch_request(
    `/api/group_invite_links/lookup?token=${encodeURIComponent(normalizedToken)}`,
    {
      cache: 'no-store',
      timeoutMs: 8000,
    },
  );
  if (!response.ok) {
    const payload = await parseMessengerV2JsonResponse(response);
    throw buildMessengerV2RequestError(response, payload);
  }

  const payload = await parseMessengerV2JsonResponse(response);
  return payload?.group_invite_link || payload;
};

export const joinMessengerV2GroupInviteLink = async ({ token } = {}) => {
  const normalizedToken = toStringOrNull(token);
  if (!normalizedToken) {
    throw new Error('token is required');
  }

  const response = await fetch_request('/api/group_invite_links/join', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: normalizedToken }),
  });
  if (!response.ok) {
    const payload = await parseMessengerV2JsonResponse(response);
    throw buildMessengerV2RequestError(response, payload);
  }

  return parseMessengerV2JsonResponse(response);
};

export const removeMessengerV2GroupMember = async ({
  groupId,
  groupMemberId,
  deleteForMe = false,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  const normalizedGroupMemberId = toStringOrNull(groupMemberId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required');
  }
  if (!normalizedGroupMemberId) {
    throw new Error('groupMemberId is required');
  }

  const response = await fetch_request(
    `/api/groups/${encodeURIComponent(normalizedGroupId)}/group_members/${encodeURIComponent(normalizedGroupMemberId)}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ delete_for_me: Boolean(deleteForMe) }),
    },
  );
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return null;
  }

  const payload = await response.json().catch(() => null);
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  return {
    ...payload,
    conversation:
      payload.conversation && typeof payload.conversation === 'object'
        ? normalizeConversation(payload.conversation)
        : payload.conversation,
  };
};

export const updateMessengerV2GroupMember = async ({
  groupId,
  groupMemberId,
  role,
  requirePostApproval,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  const normalizedGroupMemberId = toStringOrNull(groupMemberId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required');
  }
  if (!normalizedGroupMemberId) {
    throw new Error('groupMemberId is required');
  }

  const groupMember = {};
  const normalizedRole = toStringOrNull(role);
  if (normalizedRole) groupMember.role = normalizedRole;
  if (requirePostApproval !== undefined) {
    groupMember.require_post_approval = Boolean(requirePostApproval);
  }

  const response = await fetch_request(
    `/api/groups/${encodeURIComponent(normalizedGroupId)}/group_members/${encodeURIComponent(normalizedGroupMemberId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ group_member: groupMember }),
    },
  );
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = await response.json();
  return payload?.group_member || payload;
};

export const createMessengerV2Conversation = async ({
  participantId,
  currentUserId,
  currentUserName = null,
  preferredCiphersuite = DEFAULT_MLS_CIPHERSUITE,
  manager = null,
} = {}) => {
  const normalizedParticipantId = toStringOrNull(participantId);
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  if (!normalizedParticipantId) {
    throw new Error('participantId is required');
  }
  if (!normalizedCurrentUserId) {
    throw new Error('currentUserId is required');
  }

  const identityName = currentUserName || `user-${normalizedCurrentUserId}`;
  const resolvedManager =
    manager ||
    resolveMessengerV2Manager({
      userId: normalizedCurrentUserId,
      identityName,
      preferredCiphersuite,
      wantPQ: true,
      serverManaged: true,
    });
  if (!resolvedManager) {
    throw new Error('V2 MLS manager unavailable for create');
  }

  const identity =
    (await resolvedManager.ensureIdentity?.({
      name: identityName,
      ciphersuite: preferredCiphersuite,
    })) ?? null;
  const identityId =
    toStringOrNull(identity?.identityId ?? identity?.identity_id ?? null) ??
    toStringOrNull(resolvedManager.identityClientId ?? null) ??
    null;
  if (!identityId) {
    throw new Error('MLS identity unavailable for V2 create');
  }

  const clientId =
    toStringOrNull(identity?.clientId ?? identity?.client_id ?? null) ??
    toStringOrNull(resolvedManager.identityClientId ?? null) ??
    identityId;
  const headers = { 'Content-Type': 'application/json' };
  if (clientId) {
    headers['X-MLS-Client-Id'] = clientId;
  }

  const response = await fetch_request('/api/conversations', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      participant_id: normalizedParticipantId,
      ...(clientId ? { mls_client_id: clientId } : {}),
    }),
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload = await response.json();
  const snapshot = payload?.mls_group ?? null;
  const rawConversation = payload?.conversation ?? payload;
  const conversationAction = toStringOrNull(payload?.conversation_action ?? null);
  const conversation = normalizeConversation(
    patchConversationWithMlsMeta(rawConversation, snapshot, preferredCiphersuite),
  );

  if (snapshot) {
    await Promise.resolve(
      applyMessengerV2Snapshot({
        manager: resolvedManager,
        snapshot,
        userId: normalizedCurrentUserId,
        identityName,
        preferredCiphersuite,
        source: 'messages_v2_create_response',
        conversationId: conversation?.id ?? null,
      }),
    ).catch(() => null);
  }

  return {
    conversation,
    conversationAction,
    snapshot,
    clientId,
    identityId,
    manager: resolvedManager,
  };
};

export const openMessengerV2GroupConversation = async ({
  groupId,
  currentUserId,
  currentUserName = null,
  preferredCiphersuite = DEFAULT_MLS_CIPHERSUITE,
  manager = null,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  const normalizedCurrentUserId = toStringOrNull(currentUserId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required');
  }
  if (!normalizedCurrentUserId) {
    throw new Error('currentUserId is required');
  }

  const identityName = currentUserName || `user-${normalizedCurrentUserId}`;
  const resolvedManager =
    manager ||
    resolveMessengerV2Manager({
      userId: normalizedCurrentUserId,
      identityName,
      preferredCiphersuite,
      wantPQ: true,
      serverManaged: true,
    });
  if (!resolvedManager) {
    throw new Error('V2 MLS manager unavailable for group open');
  }

  const identity =
    (await resolvedManager.ensureIdentity?.({
      name: identityName,
      ciphersuite: preferredCiphersuite,
    })) ?? null;
  const identityId =
    toStringOrNull(identity?.identityId ?? identity?.identity_id ?? null) ??
    toStringOrNull(resolvedManager.identityClientId ?? null) ??
    null;
  if (!identityId) {
    throw new Error('MLS identity unavailable for group open');
  }

  const clientId =
    toStringOrNull(identity?.clientId ?? identity?.client_id ?? null) ??
    toStringOrNull(resolvedManager.identityClientId ?? null) ??
    identityId;
  const headers = {};
  if (clientId) {
    headers['X-MLS-Client-Id'] = clientId;
  }

  const response = await fetch_request(
    `/api/groups/${encodeURIComponent(normalizedGroupId)}/conversation`,
    {
      headers,
      cache: 'no-store',
    },
  );
  if (!response.ok) {
    throw createHttpError(response);
  }

  const rawConversation = await response.json();
  if (toStringOrNull(rawConversation?.kind ?? null) === 'room') {
    throw new Error('Room conversations cannot be opened in Messenger Groups');
  }
  const snapshot = rawConversation?.mls_group_id
    ? {
        group_id: rawConversation.mls_group_id,
        ciphersuite: rawConversation.mls_ciphersuite ?? preferredCiphersuite,
      }
    : null;
  const conversation = normalizeConversation(
    patchConversationWithMlsMeta(rawConversation, snapshot, preferredCiphersuite),
  );

  if (snapshot) {
    await Promise.resolve(
      applyMessengerV2Snapshot({
        manager: resolvedManager,
        snapshot,
        userId: normalizedCurrentUserId,
        identityName,
        preferredCiphersuite,
        source: 'messages_v2_group_conversation_response',
        conversationId: conversation?.id ?? null,
      }),
    ).catch(() => null);
  }

  return {
    conversation,
    snapshot,
    clientId,
    identityId,
    manager: resolvedManager,
  };
};

const messengerV2ConversationService = {
  loadMessengerV2Conversations,
  loadMessengerV2Contacts,
  loadMessengerV2Group,
  loadMessengerV2GroupAvailableUsers,
  loadMessengerV2GroupCategories,
  createMessengerV2Group,
  inviteMessengerV2GroupMember,
  inviteMessengerV2GroupMembers,
  joinMessengerV2GroupInviteLink,
  loadMessengerV2GroupInviteLink,
  loadMessengerV2GroupInvitePreview,
  createMessengerV2Conversation,
  openMessengerV2GroupConversation,
  updateMessengerV2Group,
  updateMessengerV2GroupAvatar,
  updateMessengerV2GroupMember,
};

export default messengerV2ConversationService;
